/**
 * Admin → Voice Notes
 *
 * Lists routed voice notes scoped by location. Department staff see only the
 * notes routed to their department; super_admins see everything in scope.
 *
 * The list payload omits the audio blob — we fetch the full doc on demand
 * when the operator presses Play to keep the table light.
 */
import React, { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/AppShell";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLocation } from "@/lib/location";
import { useAuth } from "@/lib/auth";
import { socket } from "@/lib/socket";
import { playEventSound, primeAudio } from "@/lib/sound";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Play, Trash, Microphone, CheckCircle, MagnifyingGlass, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_LABEL = {
    new: { label: "New", c: "bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-900" },
    listened: { label: "Listened", c: "bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-900" },
    in_progress: { label: "In progress", c: "bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-900" },
    closed: { label: "Closed", c: "bg-gray-100 dark:bg-gray-900 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-800" },
};

export default function AdminVoiceNotes() {
    const { activeLocationId } = useLocation() || {};
    const { user } = useAuth() || {};
    const [notes, setNotes] = useState([]);
    const [activeAudio, setActiveAudio] = useState(null);
    const [audioMap, setAudioMap] = useState({});
    // Filters
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("__all__");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");

    const load = async () => {
        try {
            const params = {};
            if (statusFilter !== "__all__") params.status = statusFilter;
            if (search.trim()) params.q = search.trim();
            if (dateFrom) params.date_from = dateFrom;
            if (dateTo) params.date_to = dateTo;
            const { data } = await api.get("/voice-notes", { params });
            setNotes(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load");
        }
    };
    // Reload whenever location / filters change. Debounced search keeps the
    // server load down while the operator types.
    useEffect(() => {
        const t = setTimeout(load, search ? 300 : 0);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeLocationId, statusFilter, dateFrom, dateTo, search]);

    useEffect(() => {
        // Subscribe DIRECTLY to socket events on this page so the list
        // auto-refreshes even when nothing else (e.g. StaffDashboard) is
        // mounted to bridge events. We also play the existing notification
        // sound on `voice_note:new` to match how requests/orders alert
        // staff. Browsers block audio until the user interacts at least
        // once — primeAudio() resumes the context on first click anywhere.
        const onNew = () => {
            try { playEventSound("new_request"); } catch { /* audio may be suspended */ }
            load();
        };
        const onUpdated = () => load();
        socket.on("voice_note:new", onNew);
        socket.on("voice_note:updated", onUpdated);
        // Also listen on the window-event bridge dispatched by StaffDashboard
        // for the case where both pages happen to be mounted in tabs.
        window.addEventListener("socket:voice_note:new", onUpdated);
        window.addEventListener("socket:voice_note:updated", onUpdated);
        // Prime audio context as soon as the user clicks anywhere on the page.
        const primeOnce = () => { primeAudio(); window.removeEventListener("click", primeOnce); };
        window.addEventListener("click", primeOnce);
        return () => {
            socket.off("voice_note:new", onNew);
            socket.off("voice_note:updated", onUpdated);
            window.removeEventListener("socket:voice_note:new", onUpdated);
            window.removeEventListener("socket:voice_note:updated", onUpdated);
            window.removeEventListener("click", primeOnce);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const activeFilters = useMemo(() =>
        (statusFilter !== "__all__" ? 1 : 0) + (search ? 1 : 0) + (dateFrom ? 1 : 0) + (dateTo ? 1 : 0)
    , [statusFilter, search, dateFrom, dateTo]);

    const clearFilters = () => {
        setSearch(""); setStatusFilter("__all__"); setDateFrom(""); setDateTo("");
    };

    const play = async (note) => {
        if (!audioMap[note.id]) {
            try {
                const { data } = await api.get(`/voice-notes/${note.id}`);
                setAudioMap((m) => ({ ...m, [note.id]: data.audio_data_url }));
            } catch {
                toast.error("Couldn't load audio");
                return;
            }
        }
        setActiveAudio(note.id);
        if (note.status === "new") {
            await api.patch(`/voice-notes/${note.id}`, { status: "listened" }).catch(() => {});
            load();
        }
    };

    const setStatus = async (id, status) => {
        try { await api.patch(`/voice-notes/${id}`, { status }); load(); }
        catch { toast.error("Failed"); }
    };
    const remove = async (id) => {
        if (!window.confirm("Delete this voice note?")) return;
        try { await api.delete(`/voice-notes/${id}`); load(); }
        catch { toast.error("Failed"); }
    };

    const isAdmin = user?.role === "super_admin" || user?.role === "admin";

    return (
        <AppShell title="Voice Notes" subtitle="Listen to messages recorded from room kiosks">
            <div className="p-6">
                {/* Filter bar — works on any screen, wraps to multiple rows on
                    narrow viewports so mobile users can still filter. */}
                <Card className="bg-white dark:bg-[#0B0D10] border-gray-200 dark:border-gray-800 p-4 mb-4">
                    <div className="flex flex-wrap items-end gap-3">
                        <div className="relative w-full sm:w-[260px]">
                            <Label className="text-xs">Search room</Label>
                            <div className="relative mt-1">
                                <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <Input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className="pl-8 pr-8 h-9"
                                    placeholder="Room name…"
                                    data-testid="voice-notes-search"
                                />
                                {search && (
                                    <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                        <X size={14} />
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="w-[150px]">
                            <Label className="text-xs">Status</Label>
                            <Select value={statusFilter} onValueChange={setStatusFilter}>
                                <SelectTrigger className="h-9 mt-1" data-testid="voice-notes-filter-status"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__all__">All statuses</SelectItem>
                                    <SelectItem value="new">New</SelectItem>
                                    <SelectItem value="listened">Listened</SelectItem>
                                    <SelectItem value="in_progress">In progress</SelectItem>
                                    <SelectItem value="closed">Closed</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="w-[150px]">
                            <Label className="text-xs">From</Label>
                            <Input
                                type="date"
                                value={dateFrom}
                                onChange={(e) => setDateFrom(e.target.value)}
                                className="h-9 mt-1"
                                data-testid="voice-notes-date-from"
                            />
                        </div>
                        <div className="w-[150px]">
                            <Label className="text-xs">To</Label>
                            <Input
                                type="date"
                                value={dateTo}
                                onChange={(e) => setDateTo(e.target.value)}
                                className="h-9 mt-1"
                                data-testid="voice-notes-date-to"
                            />
                        </div>
                        {activeFilters > 0 && (
                            <Button variant="ghost" onClick={clearFilters} className="text-xs h-9 text-gray-600 dark:text-gray-400" data-testid="voice-notes-filter-clear">
                                Clear filters
                            </Button>
                        )}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-3" data-testid="voice-notes-count">
                        {notes.length} voice note{notes.length === 1 ? "" : "s"}
                        {activeFilters > 0 && ` · ${activeFilters} filter${activeFilters === 1 ? "" : "s"} active`}
                    </div>
                </Card>

                {notes.length === 0 && (
                    <Card className="p-12 text-center text-gray-400 dark:text-gray-500 bg-white dark:bg-[#0B0D10] border-gray-200 dark:border-gray-800">
                        <Microphone size={32} weight="duotone" className="mx-auto mb-2 text-gray-300 dark:text-gray-700" />
                        No voice notes match these filters.
                    </Card>
                )}

                <div className="space-y-3">
                    {notes.map((n) => {
                        const st = STATUS_LABEL[n.status] || STATUS_LABEL.new;
                        return (
                            <Card key={n.id} className="p-4 bg-white dark:bg-[#0B0D10] border-gray-200 dark:border-gray-800" data-testid={`voice-note-${n.id}`}>
                                <div className="flex items-start gap-3 flex-wrap sm:flex-nowrap">
                                    <div className="w-10 h-10 rounded-full bg-blue-50 dark:bg-blue-950/40 grid place-items-center shrink-0">
                                        <Microphone size={18} weight="duotone" className="text-blue-500 dark:text-blue-400" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <div className="font-semibold text-gray-900 dark:text-gray-100">{n.room_name || "Unknown room"}</div>
                                            <Badge className={`text-[10px] uppercase tracking-wider border ${st.c}`} variant="outline">{st.label}</Badge>
                                            <span className="text-xs text-gray-500 dark:text-gray-400">· {Math.round(n.duration_seconds || 0)}s</span>
                                            <span className="text-xs text-gray-400 dark:text-gray-500">· {new Date(n.created_at).toLocaleString()}</span>
                                        </div>
                                        {activeAudio === n.id && audioMap[n.id] && (
                                            <audio src={audioMap[n.id]} controls autoPlay className="w-full mt-3" data-testid={`voice-note-${n.id}-audio`} />
                                        )}
                                    </div>
                                    <div className="flex items-center gap-1 flex-wrap">
                                        <Button size="sm" variant="outline" onClick={() => play(n)} data-testid={`voice-note-${n.id}-play`}>
                                            <Play size={14} className="mr-1" /> Play
                                        </Button>
                                        {n.status !== "in_progress" && n.status !== "closed" && (
                                            <Button size="sm" variant="ghost" onClick={() => setStatus(n.id, "in_progress")} className="text-indigo-600 dark:text-indigo-400">
                                                <CheckCircle size={14} className="mr-1" /> Take
                                            </Button>
                                        )}
                                        {n.status !== "closed" && (
                                            <Button size="sm" variant="ghost" onClick={() => setStatus(n.id, "closed")} className="text-gray-600 dark:text-gray-400">
                                                Close
                                            </Button>
                                        )}
                                        {isAdmin && (
                                            <Button size="sm" variant="ghost" onClick={() => remove(n.id)} className="text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40">
                                                <Trash size={14} />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </Card>
                        );
                    })}
                </div>
            </div>
        </AppShell>
    );
}
